import { IndicationSet, RolodexIndication } from 'features/indicationTriage'
import { computeFfsEligibility } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/computeFfsEligibility'
import { FfsPart } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'
import { StepValidationResult } from 'features/libra/workflow/workflowStepsApi'
import { GeckoComponent, GeckoComponentModel } from 'types'

export interface ComputeFfsPartValidationArgs {
  part: FfsPart
  componentId: string | undefined
  inspectionId: string | undefined
  component: GeckoComponent | undefined
  componentModel: GeckoComponentModel | undefined
  ltaSet: IndicationSet | undefined
  indications: ReadonlyArray<RolodexIndication>
  /** Ids of indications that already have a saved report for `part`. */
  indicationIdsWithReport: ReadonlySet<string>
  isLoading: boolean
}

export interface ComputeFfsPartValidationResult {
  validation: StepValidationResult
  /** LTAs for which this part applies (at least one level eligible). */
  eligibleCount: number
  /** Eligible LTAs that already have a saved report for this part. */
  assessedCount: number
}

const PART_LABEL: Record<FfsPart, string> = {
  [FfsPart.PART_4]: 'Part 4',
  [FfsPart.PART_5]: 'Part 5',
}

/**
 * Pure completion validation for a part-scoped FFS step. Mirrors the tier
 * ladder of the old `computeFfsTriageValidation` (prerequisite checks →
 * `isLoading` → data-derived check) so a normal in-flight fetch never reports a
 * false failure, then requires that **every eligible LTA has at least one saved
 * `ffs-lta-report` for this part**.
 *
 * "≥1 saved report" (NOT `is_default`) is deliberate: `setDefaultReport` keeps
 * a single default per indication across ALL parts, so a per-part default rule
 * is unsatisfiable. A `Skipped` report is still a saved report, so intentionally
 * skipped LTAs count as complete. Ineligible LTAs (part does not apply) are
 * excluded.
 */
export function computeFfsPartValidation({
  part,
  componentId,
  inspectionId,
  component,
  componentModel,
  ltaSet,
  indications,
  indicationIdsWithReport,
  isLoading,
}: ComputeFfsPartValidationArgs): ComputeFfsPartValidationResult {
  const eligibleIndications = indications.filter((indication) => {
    const { level1Eligible, level2Eligible } = computeFfsEligibility({
      componentModel,
      part,
      indication,
    })
    return level1Eligible || level2Eligible
  })
  const eligibleCount = eligibleIndications.length
  const assessedCount = eligibleIndications.filter((indication) =>
    indicationIdsWithReport.has(indication.id)
  ).length

  let validation: StepValidationResult = undefined
  const label = PART_LABEL[part]

  if (!inspectionId || !componentId) {
    validation = {
      status: 'Error',
      message: 'Ticket is missing required component or inspection tag.',
    }
  } else if (isLoading) {
    validation = { status: 'Warning', message: 'Loading indication data...' }
  } else if (!component) {
    validation = {
      status: 'Error',
      message: 'Component entry not found in Rolodex.',
    }
  } else if (!ltaSet) {
    validation = {
      status: 'Warning',
      message:
        'No LTA indication set found for this (component, inspection) yet. Generate one upstream before assessment.',
    }
  } else if (indications.length === 0) {
    validation = {
      status: 'Warning',
      message:
        'Indication set exists but has no indications -- re-run LTA generation if expected.',
    }
  } else if (eligibleCount === 0) {
    // Part does not apply to any LTA -- nothing to do, allow advancing.
    validation = undefined
  } else if (assessedCount < eligibleCount) {
    const remaining = eligibleCount - assessedCount
    validation = {
      status: 'Error',
      message: `${remaining} of ${eligibleCount} LTA${eligibleCount === 1 ? '' : 's'} still need a ${label} assessment.`,
    }
  }

  return { validation, eligibleCount, assessedCount }
}
